import { DateTime } from 'luxon';

/** Format a payment amount from minor units (cents). */
export function formatCurrency(amountMinorUnits: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() })
    .format(amountMinorUnits / 100);
}

/**
 * Telegram update structure (minimal typing for ID extraction).
 * Supports message, callback_query, inline_query, edited_message, etc.
 */
export interface TelegramUpdateForIdExtraction {
  update_id?: number;
  message?: { from?: { id: number } };
  callback_query?: { from?: { id: number } };
  inline_query?: { from?: { id: number } };
  edited_message?: { from?: { id: number } };
  channel_post?: { from?: { id: number } };
  edited_channel_post?: { from?: { id: number } };
  chosen_inline_result?: { from?: { id: number } };
  shipping_query?: { from?: { id: number } };
  pre_checkout_query?: { from?: { id: number } };
  poll_answer?: { user?: { id: number } };
  my_chat_member?: { from?: { id: number } };
  chat_member?: { from?: { id: number } };
  chat_join_request?: { from?: { id: number } };
}

/**
 * Extract the Telegram user ID from any update type.
 * Returns the ID as a string (matching telegramId field format in database).
 *
 * @param update - The Telegram update object
 * @returns The user's Telegram ID as string, or null if not extractable
 *
 * @example
 * ```typescript
 * const update = { message: { from: { id: 123456789 } } };
 * extractTelegramId(update); // '123456789'
 * ```
 */
export function extractTelegramId(update: TelegramUpdateForIdExtraction): string | null {
  // Check all possible update types in order of most common
  const id =
    update.message?.from?.id ??
    update.callback_query?.from?.id ??
    update.inline_query?.from?.id ??
    update.edited_message?.from?.id ??
    update.channel_post?.from?.id ??
    update.edited_channel_post?.from?.id ??
    update.chosen_inline_result?.from?.id ??
    update.shipping_query?.from?.id ??
    update.pre_checkout_query?.from?.id ??
    update.poll_answer?.user?.id ??
    update.my_chat_member?.from?.id ??
    update.chat_member?.from?.id ??
    update.chat_join_request?.from?.id;

  return id !== undefined ? String(id) : null;
}

/**
 * Escape HTML special characters to prevent XSS in Telegram HTML parse mode.
 *
 * Telegram supports: <b>, <i>, <u>, <s>, <a href="">, <code>, <pre>
 * All user-provided text must be escaped before inclusion in message templates.
 *
 * @param text - The text to escape
 * @returns HTML-escaped text safe for Telegram HTML parse mode
 *
 * @example
 * ```typescript
 * escapeHtml('Tom & Jerry') // 'Tom &amp; Jerry'
 * escapeHtml('<script>alert("xss")</script>') // '&lt;script&gt;alert(&quot;xss&quot;)&lt;/script&gt;'
 * ```
 */
export function escapeHtml(text: string): string {
  // IMPORTANT: Escape & FIRST to prevent double-escaping
  // (e.g., if we did < first, then &lt; would become &amp;lt;)
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Format a datetime for display in Telegram messages.
 * Output format: "Tuesday, Oct 15 at 2:00 PM"
 *
 * Note: Forces 'en' locale to ensure consistent output regardless of server locale.
 * This is important for Telegram messages which should display consistently.
 *
 * @param dateTime - Date object or ISO string
 * @param timezone - IANA timezone (e.g., 'America/New_York', 'UTC')
 * @returns Formatted string in the specified timezone (always English)
 *
 * @example
 * ```typescript
 * formatDateTime(new Date('2024-10-15T18:00:00Z'), 'America/New_York')
 * // "Tuesday, Oct 15 at 2:00 PM"
 * ```
 */
export function formatDateTime(
  dateTime: Date | string,
  timezone: string
): string {
  // notif-tz: a zone-less ISO string is a UTC instant — parse it as UTC then convert
  // to the target zone. Strings with an explicit offset (Z / ±hh:mm) keep their instant.
  const dt =
    typeof dateTime === 'string'
      ? DateTime.fromISO(dateTime, { zone: 'utc' }).setZone(timezone)
      : DateTime.fromJSDate(dateTime, { zone: timezone });

  if (!dt.isValid) {
    // Fallback for invalid timezone - use UTC
    const fallbackDt =
      typeof dateTime === 'string'
        ? DateTime.fromISO(dateTime, { zone: 'UTC' })
        : DateTime.fromJSDate(dateTime, { zone: 'UTC' });
    return fallbackDt.setLocale('en').toFormat("EEEE, LLL d 'at' h:mm a");
  }

  return dt.setLocale('en').toFormat("EEEE, LLL d 'at' h:mm a");
}

/**
 * Format a short datetime for compact displays (e.g., event lists).
 * Output format: "Oct 15, 2:00 PM"
 *
 * Note: Forces 'en' locale to ensure consistent output regardless of server locale.
 * This is important for Telegram messages which should display consistently.
 *
 * @param dateTime - Date object or ISO string
 * @param timezone - IANA timezone (e.g., 'America/New_York', 'UTC')
 * @returns Short formatted string in the specified timezone (always English)
 *
 * @example
 * ```typescript
 * formatShortDateTime(new Date('2024-10-15T18:00:00Z'), 'America/New_York')
 * // "Oct 15, 2:00 PM"
 * ```
 */
export function formatShortDateTime(
  dateTime: Date | string,
  timezone: string
): string {
  // notif-tz: a zone-less ISO string is a UTC instant — parse it as UTC then convert
  // to the target zone. Strings with an explicit offset (Z / ±hh:mm) keep their instant.
  const dt =
    typeof dateTime === 'string'
      ? DateTime.fromISO(dateTime, { zone: 'utc' }).setZone(timezone)
      : DateTime.fromJSDate(dateTime, { zone: timezone });

  if (!dt.isValid) {
    const fallbackDt =
      typeof dateTime === 'string'
        ? DateTime.fromISO(dateTime, { zone: 'UTC' })
        : DateTime.fromJSDate(dateTime, { zone: 'UTC' });
    return fallbackDt.setLocale('en').toFormat('LLL d, h:mm a');
  }

  return dt.setLocale('en').toFormat('LLL d, h:mm a');
}
